import { createSelector } from 'reselect';
import { CommanderThunkAction, AsyncAction } from '@mews-commander/types';
import { RouteName } from '@mews-commander/enums';
import { getStringProp } from '@mews/fp';
import {
    AvailabilityBlockParameters,
    MultipleAvailabilityBlockAddParameters,
    MultipleAvailabilityBlockDeleteParameters,
    MultipleAvailabilityBlockUpdateParameters,
} from '@mews-commander/data-access';
import { getResolveRoute } from '@mews-commander/legacy-app-instance';
import { createFetchAction, mapId } from '@mews-commander/utils';
import prefix from '../prefix';
import { setAvailabilityBlocks, addAvailabilityBlocksFromState } from '../ducks/availabilityBlocks';
import { setServiceOrders } from '../ducks/serviceOrders';
import { setAvailabilityAdjustments, deleteAvailabilityAdjustmentsByBlockIds } from '../ducks/availabilityAdjustments';
import { setProfiles } from '../ducks/profiles';

const AVAILABILITY_BLOCKS_FETCH = prefix('AVAILABILITY_BLOCKS_FETCH');
const AVAILABILITY_BLOCKS_ADD = prefix('AVAILABILITY_BLOCKS_ADD');
const AVAILABILITY_BLOCKS_DELETE = prefix('AVAILABILITY_BLOCKS_DELETE');
const AVAILABILITY_BLOCKS_UPDATE = prefix('AVAILABILITY_BLOCKS_UPDATE');

export const fetchAvailabilityBlocks =
    (params: AvailabilityBlockParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.availabilityBlocks
            .getAll(params)
            .then(({ availabilityBlocks, serviceOrders, adjustments, profiles }) => {
                if (availabilityBlocks?.length) {
                    if (params.extent?.adjustments) {
                        const availabilityBlockIds = mapId(availabilityBlocks);

                        dispatch(deleteAvailabilityAdjustmentsByBlockIds(availabilityBlockIds));
                    }

                    dispatch(setAvailabilityBlocks(availabilityBlocks));
                }
                if (serviceOrders?.length) {
                    dispatch(setServiceOrders({ serviceOrders }));
                }
                if (adjustments?.length) {
                    dispatch(setAvailabilityAdjustments(adjustments));
                }
                if (profiles?.length) {
                    dispatch(setProfiles(profiles));
                }
            });

        return dispatch(createFetchAction(AVAILABILITY_BLOCKS_FETCH, promise));
    };

export const addAvailabilityBlocks =
    (params: MultipleAvailabilityBlockAddParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.availabilityBlocks.add(params).then(({ availabilityBlocks: blocks }) => {
            if (blocks.length) {
                dispatch(addAvailabilityBlocksFromState(blocks));
            }
            return { availabilityBlocks: blocks };
        });

        return dispatch(createFetchAction(AVAILABILITY_BLOCKS_ADD, promise));
    };

export const deleteAvailabilityBlocks =
    (params: MultipleAvailabilityBlockDeleteParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.availabilityBlocks.delete(params);

        return dispatch(createFetchAction(AVAILABILITY_BLOCKS_DELETE, promise));
    };

export const updateAvailabilityBlocks =
    ({ availabilityBlocks }: MultipleAvailabilityBlockUpdateParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.availabilityBlocks
            .update({ availabilityBlocks })
            .then(({ availabilityBlocks: blocks }) => {
                if (blocks?.length) {
                    dispatch(setAvailabilityBlocks(blocks));
                }
            });
        return dispatch(createFetchAction(AVAILABILITY_BLOCKS_UPDATE, promise));
    };

/**
 * @deprecated use useAvailabilityBlockInfo
 */
export const getAvailabilityBlockUrl = createSelector(getResolveRoute, getStringProp('id'), (resolveRoute, id) =>
    resolveRoute(RouteName.AvailabilityBlockDashboard, { availabilityBlockId: id })
);
